import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

const MonthYearSelector = ({ selectedDate, onDateChange, onDataLoad }) => {
  const { user } = useAuth()
  const { t, i18n } = useTranslation(['dashboard', 'common'])
  const [availableData, setAvailableData] = useState(new Set())
  const [loading, setLoading] = useState(false)
  const locale = i18n.language === 'es' ? 'es-ES' : 'en-US'

  const capitalizeFirstLetter = (value) =>
    value.charAt(0).toLocaleUpperCase(locale) + value.slice(1)

  useEffect(() => {
    fetchAvailableData()
  }, [user])

  const fetchAvailableData = async () => {
    if (!user) return

    try {
      setLoading(true)
      
      // Get user's business
      const { data: business } = await supabase
        .from('businesses')
        .select('id')
        .eq('owner_email', user.email)
        .single()

      if (!business) return

      // Fetch all months with data
      const { data: metrics } = await supabase
        .from('monthly_metrics')
        .select('month, year')
        .eq('business_id', business.id)

      // Create a set of available date keys
      const dataKeys = new Set(
        metrics?.map(m => `${m.year}-${String(m.month).padStart(2, '0')}`) || []
      )
      
      setAvailableData(dataKeys)
    } catch (error) {
      console.error('Error fetching available data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getCurrentDateKey = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
  }

  const hasData = (date) => {
    return availableData.has(getCurrentDateKey(date))
  }

  const navigateMonth = (direction) => {
    const newDate = new Date(selectedDate)
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1)
    } else {
      newDate.setMonth(newDate.getMonth() + 1)
    }
    onDateChange(newDate)
  }

  const handleMonthChange = (e) => {
    const newMonth = parseInt(e.target.value)
    const newDate = new Date(selectedDate.getFullYear(), newMonth, 1)
    onDateChange(newDate)
  }

  const handleYearChange = (e) => {
    const newYear = parseInt(e.target.value)
    const newDate = new Date(newYear, selectedDate.getMonth(), 1)
    onDateChange(newDate)
  }

  const formatDateDisplay = (date) => {
    return capitalizeFirstLetter(date.toLocaleDateString(locale, {
      month: 'long', 
      year: 'numeric' 
    }))
  }

  // Generate year options (current year ± 5 years)
  const currentYear = new Date().getFullYear()
  const yearOptions = []
  for (let year = currentYear - 5; year <= currentYear + 1; year++) {
    yearOptions.push(year)
  }

  const monthNames = [
    t('months.january'),
    t('months.february'),
    t('months.march'),
    t('months.april'),
    t('months.may'),
    t('months.june'),
    t('months.july'),
    t('months.august'),
    t('months.september'),
    t('months.october'),
    t('months.november'),
    t('months.december')
  ]

  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-5">
        <h3 className="text-lg font-semibold tracking-tight text-gray-900">{t('dataEntry.monthSelector.selectMonth')}</h3>
        <div className="flex items-center gap-2 text-xs sm:text-sm">
          {loading ? (
            <span className="text-gray-500">{t('status.loading', { ns: 'common' })}</span>
          ) : (
            <>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-green-700">
                <span aria-hidden="true" className="h-2 w-2 rounded-full bg-green-600" />
                {t('dataEntry.monthSelector.hasData')}
              </span>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-gray-500">
                <span aria-hidden="true" className="h-2 w-2 rounded-full bg-gray-400" />
                {t('dataEntry.monthSelector.noData')}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="flex flex-wrap items-center justify-between gap-1 mb-5">
        <button
          onClick={() => navigateMonth('prev')}
          className="flex items-center px-2 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF1B54]"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('buttons.previous', { ns: 'common' })}
        </button>

        <div className="flex items-center justify-center gap-1 flex-1 min-w-[8rem] px-1">
          {hasData(selectedDate) ? (
            <span className="inline-flex items-center text-green-600">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </span>
          ) : (
            <span className="inline-flex items-center text-gray-400">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.966-.833-2.732 0L3.732 14.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </span>
          )}
          <span className="text-sm font-semibold text-gray-900 text-center leading-tight">
            {formatDateDisplay(selectedDate)}
          </span>
        </div>

        <button
          onClick={() => navigateMonth('next')}
          disabled={selectedDate.getTime() >= new Date().setDate(1)}
          className="flex items-center px-2 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF1B54] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('buttons.next', { ns: 'common' })}
          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Dropdown Selectors */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <div>
          <label htmlFor="month-select" className="block text-sm font-semibold text-gray-700 mb-2">
            {t('month', { ns: 'common' })}
          </label>
          <select
            id="month-select"
            value={selectedDate.getMonth()}
            onChange={handleMonthChange}
            className="block w-full h-11 border border-gray-300 rounded-lg px-3 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:border-[#FF1B54]"
          >
            {monthNames.map((month, index) => (
              <option key={index} value={index}>
                {capitalizeFirstLetter(month)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="year-select" className="block text-sm font-semibold text-gray-700 mb-2">
            {t('customer.year')}
          </label>
          <select
            id="year-select"
            value={selectedDate.getFullYear()}
            onChange={handleYearChange}
            className="block w-full h-11 border border-gray-300 rounded-lg px-3 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:border-[#FF1B54]"
          >
            {yearOptions.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="mt-5 pt-4 border-t border-gray-200">
        <p className="text-sm text-gray-500 mb-3">{t('customer.quickJump')}</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onDateChange(new Date())}
            className="px-3 py-1.5 text-sm font-medium text-[#FF1B54] bg-[#FFF0F4] border border-[#FFC7D6] rounded-lg hover:bg-[#FFDCE7] transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:ring-offset-1"
          >
            {t('dataEntry.monthSelector.currentMonth')}
          </button>
          <button
            onClick={() => {
              const lastMonth = new Date()
              lastMonth.setMonth(lastMonth.getMonth() - 1)
              onDateChange(lastMonth)
            }}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:ring-offset-1"
          >
            {t('dataEntry.monthSelector.lastMonth')}
          </button>
          <button
            onClick={() => {
              const startOfYear = new Date(new Date().getFullYear(), 0, 1)
              onDateChange(startOfYear)
            }}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:ring-offset-1"
          >
            {t('dataEntry.monthSelector.startOfYear')}
          </button>
        </div>
      </div>

      {/* Data Status */}
      {hasData(selectedDate) && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          {t('customer.dataAvailableFor', { period: formatDateDisplay(selectedDate) })}
        </div>
      )}
    </div>
  )
}

export default MonthYearSelector
